/**
 * Minimal, framework-agnostic fetch client for Fluid APIs
 * Compatible with fluid-admin patterns but usable standalone
 */

export interface FetchClientConfig {
  /**
   * Base URL for all requests (e.g., "https://api.fluid.app/api")
   */
  baseUrl: string;

  /**
   * Optional function to get auth token
   * Return null/undefined if no token available
   */
  getAuthToken?: () => string | null | Promise<string | null>;

  /**
   * Optional callback when 401 auth error occurs
   */
  onAuthError?: () => void;

  /**
   * Default headers to include in all requests
   * Example: { "x-fluid-client": "admin" }
   */
  defaultHeaders?: Record<string, string>;

  /**
   * Credentials mode for fetch requests.
   * Set to `"include"` for cookie-based (same-origin BFF) authentication.
   * @default undefined (browser default: "same-origin")
   */
  credentials?: RequestCredentials;

  /**
   * Request cache mode for fetch requests.
   * @default undefined (browser default)
   */
  cache?: RequestCache;

  /**
   * Retry configuration for thrown network errors from fetch.
   * Does not retry HTTP error responses or aborted requests.
   * @default undefined (no retries)
   */
  networkRetry?: {
    maxRetries?: number;
    baseDelayMs?: number;
  };

  /**
   * Throw ApiError when a successful response declares JSON but cannot be parsed.
   * Defaults to false to preserve the legacy generated-client behavior.
   * @default false
   */
  throwOnInvalidJson?: boolean;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  body?: unknown;
  signal?: AbortSignal;
  priority?: RequestInit["priority"];
}

/**
 * API Error class compatible with fluid-admin's ApiError
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;

    if ("captureStackTrace" in Error) {
      (
        Error as {
          captureStackTrace: (
            target: Error,
            constructor: NewableFunction,
          ) => void;
        }
      ).captureStackTrace(this, ApiError);
    }
  }

  toJSON(): { name: string; message: string; status: number; data: unknown } {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      data: this.data,
    };
  }
}

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export interface FetchClientInstance {
  request: <TResponse = unknown>(
    endpoint: string,
    options?: RequestOptions,
  ) => Promise<TResponse>;
  requestWithFormData: <TResponse = unknown>(
    endpoint: string,
    formData: FormData,
    options?: Omit<RequestOptions, "body" | "params"> & {
      method?: "POST" | "PUT" | "PATCH";
    },
  ) => Promise<TResponse>;
  get: <TResponse = unknown>(
    endpoint: string,
    params?: Record<string, unknown>,
    options?: Omit<RequestOptions, "method" | "params">,
  ) => Promise<TResponse>;
  post: <TResponse = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => Promise<TResponse>;
  put: <TResponse = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => Promise<TResponse>;
  patch: <TResponse = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => Promise<TResponse>;
  delete: <TResponse = unknown>(
    endpoint: string,
    options?: Omit<RequestOptions, "method">,
  ) => Promise<TResponse>;
}

/**
 * Creates a configured fetch client instance
 */
export function createFetchClient(
  config: FetchClientConfig,
): FetchClientInstance {
  const {
    baseUrl,
    getAuthToken,
    onAuthError,
    defaultHeaders = {},
    credentials,
    cache,
    networkRetry,
    throwOnInvalidJson = false,
  } = config;
  const maxNetworkRetries = Math.max(0, networkRetry?.maxRetries ?? 0);
  const baseNetworkRetryDelayMs = Math.max(0, networkRetry?.baseDelayMs ?? 0);

  /**
   * Build headers for a request
   */
  async function buildHeaders(
    customHeaders?: Record<string, string>,
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...defaultHeaders,
      ...customHeaders,
    };

    // Add auth token if available
    if (getAuthToken) {
      const token = await getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Join baseUrl + endpoint via string concatenation (matches fetchApi).
   * Using `new URL(endpoint, baseUrl)` would strip any path prefix from
   * baseUrl (e.g. "/api") when the endpoint starts with "/".
   */
  function joinUrl(endpoint: string): string {
    return `${baseUrl}${endpoint}`;
  }

  /**
   * Build URL with query parameters for GET requests
   * Compatible with fluid-admin's query param handling
   */
  function buildUrl(
    endpoint: string,
    params?: Record<string, unknown>,
  ): string {
    const fullUrl = joinUrl(endpoint);

    if (!params || Object.keys(params).length === 0) {
      return fullUrl;
    }

    const queryString = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return; // Skip undefined/null values
      }

      if (Array.isArray(value)) {
        // Handle arrays like Rails expects: key[]
        value.forEach((item) => queryString.append(`${key}[]`, String(item)));
      } else if (typeof value === "object") {
        // Handle nested objects: key[subkey]
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (subValue === undefined || subValue === null) {
            return;
          }

          if (Array.isArray(subValue)) {
            subValue.forEach((item) =>
              queryString.append(`${key}[${subKey}][]`, String(item)),
            );
          } else {
            queryString.append(`${key}[${subKey}]`, String(subValue));
          }
        });
      } else {
        queryString.append(key, String(value));
      }
    });

    const qs = queryString.toString();
    return qs ? `${fullUrl}?${qs}` : fullUrl;
  }

  /**
   * Shared response handler for both JSON and FormData requests.
   * Handles auth errors, non-OK responses, 204 No Content, and JSON parsing.
   */
  async function handleResponse<TResponse>(
    response: Response,
    method: string,
    _url: string,
  ): Promise<TResponse> {
    if (response.status === 401 && onAuthError) {
      onAuthError();
    }

    if (!response.ok) {
      // Read body as text first to avoid SyntaxError from response.json()
      // when server returns non-JSON bodies with application/json content-type.
      const errorText = await response.text().catch(() => "");
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        let data: Record<string, unknown>;
        try {
          data = JSON.parse(errorText);
        } catch {
          throw new ApiError(
            errorText.slice(0, 200) ||
              `${method} request failed with status ${response.status}`,
            response.status,
            null,
          );
        }
        // Some Rails BFF endpoints return `{ error: { message, details } }`
        // instead of `{ message }` or `{ error_message }`. Fall through to
        // that shape last so callers always surface the real reason.
        const nestedError =
          typeof data.error === "object" && data.error !== null
            ? (data.error as { message?: unknown }).message
            : undefined;
        const msg =
          (data.message as string | undefined) ||
          (data.error_message as string | undefined) ||
          (typeof nestedError === "string" ? nestedError : undefined);
        throw new ApiError(
          msg || `${method} request failed`,
          response.status,
          data.errors || data,
        );
      } else {
        throw new ApiError(
          `${method} request failed with status ${response.status}`,
          response.status,
          null,
        );
      }
    }

    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return null as TResponse;
    }

    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const responseText = await response.text();

      try {
        const data = JSON.parse(responseText);
        return data as TResponse;
      } catch {
        if (throwOnInvalidJson) {
          throw new ApiError(
            "Failed to parse response as JSON",
            response.status,
            null,
          );
        }

        // API declared JSON content-type but body isn't valid JSON.
        // Return the raw payload to preserve the legacy non-strict path.
        return responseText ? (responseText as TResponse) : (null as TResponse);
      }
    }

    // Non-JSON response (text/plain, text/html, etc.)
    return null as TResponse;
  }

  function getNetworkRetryDelayMs(retryAttempt: number): number {
    return baseNetworkRetryDelayMs * 2 ** (retryAttempt - 1);
  }

  async function waitForNetworkRetry(retryAttempt: number): Promise<void> {
    const delayMs = getNetworkRetryDelayMs(retryAttempt);
    if (delayMs <= 0) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  async function fetchWithNetworkRetry(
    url: string,
    fetchOptions: RequestInit,
    signal?: AbortSignal,
  ): Promise<Response> {
    let retryCount = 0;

    while (true) {
      try {
        return await fetch(url, fetchOptions);
      } catch (networkError) {
        if (signal?.aborted || retryCount >= maxNetworkRetries) {
          throw networkError;
        }

        retryCount += 1;
        await waitForNetworkRetry(retryCount);

        if (signal?.aborted) {
          throw networkError;
        }
      }
    }
  }

  /**
   * Main request function
   */
  async function request<TResponse = unknown>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<TResponse> {
    const {
      method = "GET",
      headers: customHeaders,
      params,
      body,
      signal,
      priority,
    } = options;

    const url = params ? buildUrl(endpoint, params) : joinUrl(endpoint);

    const headers = await buildHeaders(customHeaders);

    let response: Response;

    try {
      const fetchOptions: RequestInit = { method, headers };
      if (credentials) fetchOptions.credentials = credentials;
      if (cache) fetchOptions.cache = cache;
      if (priority) fetchOptions.priority = priority;
      const serializedBody =
        body && method !== "GET" ? JSON.stringify(body) : null;
      if (serializedBody) fetchOptions.body = serializedBody;
      if (signal) fetchOptions.signal = signal;
      response = await fetchWithNetworkRetry(url, fetchOptions, signal);
    } catch (networkError) {
      throw new ApiError(
        `Network error: ${networkError instanceof Error ? networkError.message : "Unknown network error"}`,
        0,
        null,
      );
    }

    return handleResponse<TResponse>(response, method, url);
  }

  /**
   * Request with FormData (for file uploads)
   */
  async function requestWithFormData<TResponse = unknown>(
    endpoint: string,
    formData: FormData,
    options: Omit<RequestOptions, "body" | "params"> & {
      method?: "POST" | "PUT" | "PATCH";
    } = {},
  ): Promise<TResponse> {
    const {
      method = "POST",
      headers: customHeaders,
      signal,
      priority,
    } = options;

    const url = joinUrl(endpoint);
    const headers = await buildHeaders(customHeaders);

    // Remove Content-Type to let browser set it with boundary
    delete headers["Content-Type"];

    let response: Response;

    try {
      const fetchOptions: RequestInit = { method, headers, body: formData };
      if (credentials) fetchOptions.credentials = credentials;
      if (cache) fetchOptions.cache = cache;
      if (priority) fetchOptions.priority = priority;
      if (signal) fetchOptions.signal = signal;
      response = await fetchWithNetworkRetry(url, fetchOptions, signal);
    } catch (networkError) {
      throw new ApiError(
        `Network error: ${networkError instanceof Error ? networkError.message : "Unknown network error"}`,
        0,
        null,
      );
    }

    return handleResponse<TResponse>(response, method, url);
  }

  // Return client with convenience methods
  return {
    request: request,
    requestWithFormData: requestWithFormData,

    // Convenience methods for common HTTP verbs
    get: <TResponse = unknown>(
      endpoint: string,
      params?: Record<string, unknown>,
      options?: Omit<RequestOptions, "method" | "params">,
    ): Promise<TResponse> =>
      request<TResponse>(endpoint, {
        ...options,
        method: "GET" as const,
        ...(params && { params }),
      }),

    post: <TResponse = unknown>(
      endpoint: string,
      body?: unknown,
      options?: Omit<RequestOptions, "method" | "body">,
    ): Promise<TResponse> =>
      request<TResponse>(endpoint, {
        ...options,
        method: "POST",
        body,
      }),

    put: <TResponse = unknown>(
      endpoint: string,
      body?: unknown,
      options?: Omit<RequestOptions, "method" | "body">,
    ): Promise<TResponse> =>
      request<TResponse>(endpoint, {
        ...options,
        method: "PUT",
        body,
      }),

    patch: <TResponse = unknown>(
      endpoint: string,
      body?: unknown,
      options?: Omit<RequestOptions, "method" | "body">,
    ): Promise<TResponse> =>
      request<TResponse>(endpoint, {
        ...options,
        method: "PATCH",
        body,
      }),

    delete: <TResponse = unknown>(
      endpoint: string,
      options?: Omit<RequestOptions, "method">,
    ): Promise<TResponse> =>
      request<TResponse>(endpoint, {
        ...options,
        method: "DELETE",
      }),
  };
}

export type FetchClient = FetchClientInstance;
